import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MapPin, X, AlertTriangle, CalendarClock, ArrowRight } from 'lucide-react';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import apiCoreAdapter from '@/hooks/apiCoreAdapter';
import { getTimezoneInfo, formatTimezoneName, normalizeTimezone } from '@/utils/timezone';
import { getTimezoneDisplayInfo } from '@/utils/countryTimezones';
import { usePsychologistContext } from '@/contexts/PsychologistContext';
import { useAuth } from '@/contexts/AuthContext';

const TIMEAPI_COORDINATE = 'https://timeapi.io/api/TimeZone/coordinate';
const MAPS_SCRIPT_ID = 'psi-google-maps-script';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GMap = any;

interface DbTimezone { name: string; utc_offset: string; countries?: string[]; }

interface TimeApiResponse {
  timeZone?: string;
  currentUtcOffset?: { seconds: number };
  standardUtcOffset?: { seconds: number };
}

interface PlacePrediction { description: string; place_id: string; }

function parseDbOffsetSeconds(offsetStr: string): number {
  const m = offsetStr.match(/([+-])(\d{2}):(\d{2})/);
  if (!m) return 0;
  const sign = m[1] === '+' ? 1 : -1;
  return sign * (parseInt(m[2]) * 3600 + parseInt(m[3]) * 60);
}

function normalizeName(s: string): string {
  // eslint-disable-next-line no-misleading-character-class
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/_/g, ' ').toLowerCase();
}

function findFallbackTimezone(
  detected: string,
  currentOffsetSec: number,
  standardOffsetSec: number,
  countryCode: string,
  dbList: DbTimezone[],
): DbTimezone | null {
  const exact = dbList.find(tz => tz.name === detected);
  if (exact) return null;

  const continent = detected.split('/')[0];
  const tryOffset = (offset: number, list: DbTimezone[]) =>
    list.find(tz => parseDbOffsetSeconds(tz.utc_offset) === offset);

  // 1. Mismo país (campo countries del DB) + offset actual/estándar
  if (countryCode) {
    const countryList = dbList.filter(tz => tz.countries?.includes(countryCode));
    const r = tryOffset(currentOffsetSec, countryList) ?? tryOffset(standardOffsetSec, countryList);
    if (r) return r;
  }

  // 2. Mismo continente + offset actual/estándar
  const continentList = dbList.filter(tz => tz.name.startsWith(continent + '/'));
  const r2 = tryOffset(currentOffsetSec, continentList) ?? tryOffset(standardOffsetSec, continentList);
  if (r2) return r2;

  // 3. Cualquier zona con offset coincidente
  const r3 = tryOffset(currentOffsetSec, dbList) ?? tryOffset(standardOffsetSec, dbList);
  if (r3) return r3;

  // 4. Nombre normalizado (sin acentos ni guiones bajos)
  const detectedCity = normalizeName(detected.split('/').pop() ?? detected);
  return dbList.find(tz => normalizeName(tz.name.split('/').pop() ?? tz.name) === detectedCity) ?? null;
}

function loadGoogleMapsScript(apiKey: string): Promise<void> {
  const g = (window as { google?: GMap }).google;
  if (g?.maps?.places) return Promise.resolve();
  if (document.getElementById(MAPS_SCRIPT_ID)) {
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if ((window as { google?: GMap }).google?.maps?.places) { clearInterval(check); resolve(); }
      }, 100);
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=es`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.head.appendChild(script);
  });
}

const SUBDIVISION_LABELS: Record<string, string> = {
  AR: 'Provincia', EC: 'Provincia',
  US: 'Estado', MX: 'Estado', BR: 'Estado', VE: 'Estado', AU: 'Estado',
  CL: 'Región', PE: 'Región',
  CO: 'Departamento', BO: 'Departamento', UY: 'Departamento', PY: 'Departamento',
  ES: 'Comunidad autónoma',
  DE: 'Estado federado', AT: 'Estado federado', CH: 'Cantón',
  GB: 'Región', FR: 'Región', IT: 'Región',
  CA: 'Provincia / Territorio',
};

function getSubdivisionLabel(code: string): string {
  return SUBDIVISION_LABELS[code.toUpperCase()] ?? 'Estado / Provincia';
}

const FIELD_CLASS =
  'w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white';

const PREVIEW_HOURS = [9, 14, 19];

function buildPreview(fromTz: string, toTz: string) {
  const now = new Date();
  return PREVIEW_HOURS.map((h) => {
    const base = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, 0, 0);
    const utc = fromZonedTime(base, fromTz);
    const fromTime = formatInTimeZone(utc, fromTz, 'HH:mm');
    const toTime = formatInTimeZone(utc, toTz, 'HH:mm');
    const fromDay = formatInTimeZone(utc, fromTz, 'd');
    const toDay = formatInTimeZone(utc, toTz, 'd');
    const dayDiff = parseInt(toDay) - parseInt(fromDay);
    return { fromTime, toTime, dayDiff };
  });
}

interface TimezoneModalProps {
  open: boolean;
  onClose: () => void;
  currentTimezone: string | null;
  onSaved: (timezone: string) => void;
}

export const TimezoneModal: React.FC<TimezoneModalProps> = ({ open, onClose, currentTimezone, onSaved }) => {
  const { psychologist } = usePsychologistContext();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [stateRegion, setStateRegion] = useState('');
  const [city, setCity] = useState('');
  const [timezone, setTimezone] = useState('');
  const [loadingTimezone, setLoadingTimezone] = useState(false);
  const [dbTimezones, setDbTimezones] = useState<DbTimezone[]>([]);
  const [saving, setSaving] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [mapsReady, setMapsReady] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autocompleteRef = useRef<GMap>(null);
  const placesRef = useRef<GMap>(null);
  const mapDivRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Cargar Google Maps y lista de timezones al montar
  useEffect(() => {
    apiCoreAdapter.listTimezones()
      .then(list => setDbTimezones(list))
      .catch(() => { /* noop */ });

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;
    loadGoogleMapsScript(apiKey).then(() => {
      const g = (window as { google?: GMap }).google;
      autocompleteRef.current = new g.maps.places.AutocompleteService();
      if (mapDivRef.current) placesRef.current = new g.maps.places.PlacesService(mapDivRef.current);
      setMapsReady(true);
    }).catch(() => { /* sin clave o error */ });
  }, []);

  // Reset al abrir + inicializar PlacesService cuando el DOM ya está disponible
  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
      setCountry('');
      setCountryCode('');
      setStateRegion('');
      setCity('');
      setTimezone('');
      setSaving(false);
      setAgreed(false);

      setTimeout(() => searchInputRef.current?.focus(), 50);

      // El DialogContent ya está en el DOM: inicializar PlacesService si todavía no está listo
      if (!placesRef.current && mapDivRef.current) {
        const g = (window as { google?: GMap }).google;
        if (g?.maps?.places) {
          placesRef.current = new g.maps.places.PlacesService(mapDivRef.current);
        }
      }
    }
  }, [open, currentTimezone]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 3 || !autocompleteRef.current) { setSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(() => {
      // eslint-disable-next-line no-misleading-character-class
      const normalized = value.normalize('NFD').replace(/[̀-ͯ]/g, '');
      autocompleteRef.current.getPlacePredictions(
        { input: normalized, language: 'es' },
        (preds: PlacePrediction[] | null, status: string) => {
          if (status === 'OK' && preds) { setSuggestions(preds); setShowSuggestions(true); }
          else { setSuggestions([]); setShowSuggestions(false); }
        }
      );
    }, 300);
  }, []);

  const handleSelectSuggestion = useCallback((place: PlacePrediction) => {
    setSearchQuery(place.description);
    setSuggestions([]);
    setShowSuggestions(false);
    if (!placesRef.current && mapDivRef.current) {
      const g = (window as { google?: GMap }).google;
      if (g?.maps?.places) placesRef.current = new g.maps.places.PlacesService(mapDivRef.current);
    }
    if (!placesRef.current) return;

    placesRef.current.getDetails(
      { placeId: place.place_id, fields: ['address_components', 'geometry'] },
      (details: GMap, status: string) => {
        if (status !== 'OK' || !details) return;
        const comps: GMap[] = details.address_components || [];
        const get = (type: string) => comps.find((c: GMap) => c.types.includes(type));

        const detectedCountryCode = get('country')?.short_name || '';
        setCountry(get('country')?.long_name || '');
        setCountryCode(detectedCountryCode);
        setStateRegion(get('administrative_area_level_1')?.long_name || '');
        setCity((get('locality') || get('sublocality') || get('postal_town'))?.long_name || '');

        const lat = details.geometry?.location?.lat();
        const lng = details.geometry?.location?.lng();
        if (lat === undefined || lng === undefined) return;

        setLoadingTimezone(true);
        fetch(`${TIMEAPI_COORDINATE}?latitude=${lat}&longitude=${lng}`)
          .then(r => r.json())
          .then((data: TimeApiResponse) => {
            if (!data.timeZone) return;
            // Cualquier ciudad argentina se guarda como Buenos Aires: el país tiene un solo
            // huso horario, así que no hay motivo para persistir Salta, Córdoba o Mendoza.
            const detected = normalizeTimezone(data.timeZone);
            const currentOffsetSec = data.currentUtcOffset?.seconds ?? data.standardUtcOffset?.seconds ?? 0;
            const standardOffsetSec = data.standardUtcOffset?.seconds ?? currentOffsetSec;

            const isInDb = dbTimezones.some(tz => tz.name === detected);
            if (isInDb) {
              setTimezone(detected);
            } else {
              const fallback = findFallbackTimezone(detected, currentOffsetSec, standardOffsetSec, detectedCountryCode, dbTimezones);
              setTimezone(fallback ? fallback.name : detected);
            }
            setAgreed(false);
          })
          .catch(() => { /* mantener timezone actual */ })
          .finally(() => setLoadingTimezone(false));
      }
    );
  }, [dbTimezones]);


  const handleSave = async () => {
    if (!timezone) return;
    setSaving(true);
    try {
      await apiCoreAdapter.updatePsychologistProfile({ timezone });
      try { sessionStorage.setItem('psi-tz-notice-skip-session', '1'); } catch { /* noop */ }
      onSaved(timezone);
      onClose();

      if (currentTimezone && timezone !== currentTimezone) {
        const firstName = user?.name?.split(' ')[0] || psychologist?.name?.split(' ')[0] || 'Psicóloga';
        const fromInfo = getTimezoneDisplayInfo(currentTimezone);
        const toInfo = getTimezoneDisplayInfo(timezone);
        const preview = buildPreview(currentTimezone, timezone);
        const now = new Date();
        const fechaConfirmacion =
          now.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) +
          ' a las ' +
          now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

        const arrowColor = '#22c55e';
        const toTimeColor = '#15803d';

        const previewRows = preview.map(({ fromTime, toTime, dayDiff }) => `
          <tr>
            <td style="padding:6px 12px;font-size:15px;font-weight:600;color:#374151;">${fromTime}</td>
            <td style="padding:6px 8px;font-size:18px;color:${arrowColor};text-align:center;">→</td>
            <td style="padding:6px 12px;font-size:15px;font-weight:600;color:${toTimeColor};text-align:right;">
              ${dayDiff !== 0 ? `<span style="font-size:11px;font-weight:400;color:${arrowColor};margin-right:4px;">${dayDiff > 0 ? '+1 día' : '-1 día'}</span>` : ''}${toTime}
            </td>
          </tr>`).join('');

        const html = `<!DOCTYPE html>
<html lang="es">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;background:#f9fafb;font-family:'Segoe UI',Arial,sans-serif;">
  <div style="max-width:520px;margin:32px auto;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">
    <div style="background-color:#4C1D95;background:linear-gradient(135deg,#4C1D95 0%,#6D28D9 100%);padding:28px 32px;">
      <p style="margin:0;font-size:22px;font-weight:700;color:#fff;">Cambiaste tu ubicación ✅</p>
      <p style="margin:6px 0 0;font-size:14px;color:#ddd6fe;">Psimammoliti · Psi Platform</p>
    </div>
    <div style="padding:28px 32px;">
      <p style="font-size:15px;color:#374151;margin:0 0 8px;">Hola <strong>${firstName}</strong>, ¿cómo estás?</p>
      <p style="font-size:14px;color:#6b7280;margin:0 0 20px;">
        El <strong>${fechaConfirmacion}</strong> actualizaste tu ubicación en Psi Platform.
      </p>
      <div style="background:#f5f3ff;border:1px solid #ede9fe;border-radius:12px;padding:16px 20px;margin-bottom:20px;">
        <p style="margin:0 0 12px;font-size:13px;font-weight:600;color:#6D28D9;text-transform:uppercase;letter-spacing:.05em;">Cambio registrado</p>
        <div style="display:flex;flex-direction:column;gap:12px;">
          <div>
            <p style="margin:0;font-size:11px;color:#9ca3af;text-transform:uppercase;letter-spacing:.04em;">Anterior</p>
            <p style="margin:4px 0 0;font-size:14px;font-weight:600;color:#374151;">${fromInfo.label}</p>
            <p style="margin:2px 0 0;font-size:12px;color:#9ca3af;">Huso horario: ${fromInfo.offset}</p>
          </div>
          <div>
            <p style="margin:0;font-size:11px;color:#9ca3af;text-transform:uppercase;letter-spacing:.04em;">Nuevo</p>
            <p style="margin:4px 0 0;font-size:14px;font-weight:600;color:#6D28D9;">${toInfo.label}</p>
            <p style="margin:2px 0 0;font-size:12px;color:#9ca3af;">Huso horario: ${toInfo.offset}</p>
          </div>
        </div>
        <p style="margin:12px 0 0;font-size:11px;color:#9ca3af;line-height:1.5;">El huso horario es la referencia de hora de tu ubicación; lo mostramos por si necesitás verificarlo.</p>
      </div>
      <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:12px;padding:16px 20px;margin-bottom:20px;">
        <p style="margin:0 0 12px;font-size:13px;font-weight:600;color:#374151;">¿Cómo se verán tus sesiones?</p>
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr>
              <th style="padding:4px 12px;font-size:11px;font-weight:500;color:#9ca3af;text-align:left;">${fromInfo.label.split('/').pop()?.trim()}</th>
              <th style="width:32px;"></th>
              <th style="padding:4px 12px;font-size:11px;font-weight:500;color:#9ca3af;text-align:right;">${toInfo.label.split('/').pop()?.trim()}</th>
            </tr>
          </thead>
          <tbody>${previewRows}</tbody>
        </table>
      </div>
      <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0 0 8px;">
        A partir de ahora, los horarios de tus sesiones en la plataforma se mostrarán en tu nueva zona horaria. Tus pacientes no se verán afectados.
      </p>
      <p style="font-size:12px;color:#9ca3af;margin:0;">Guardá este email como constancia del cambio.</p>
    </div>
    <div style="background:#f3f4f6;padding:16px 32px;text-align:center;">
      <p style="margin:0;font-size:12px;color:#9ca3af;">¿Dudas? Escribinos a <a href="mailto:psicos@psimammoliti.com" style="color:#6D28D9;">psicos@psimammoliti.com</a></p>
    </div>
  </div>
</body>
</html>`;

        void apiCoreAdapter.psychologistNotifyProfileChanges({
          html,
          subject: 'Cambiaste tu ubicación — Psi Platform',
        });
      }
    } finally {
      setSaving(false);
      setAgreed(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent data-testid="timezone-location-modal" className="p-0 overflow-hidden">
        <div ref={mapDivRef} aria-hidden="true" className="hidden" />

        {/* Padding uniforme en los 4 bordes — la card crece en alto según el contenido
            (título, buscador, sugerencias del autocompletado, campos, footer), con este
            div como único límite de alto (90% del viewport) y scroll interno si excede. */}
        <div className="overflow-y-auto max-h-[90svh] p-4 sm:p-6">

          <div className="flex items-center justify-between mb-5">
            <DialogTitle className="text-base font-semibold text-gray-900">
              Buscá tu ubicación actual <span className="text-primary">*</span>
            </DialogTitle>
            <DialogClose asChild>
              <button type="button" className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 transition-colors" aria-label="Cerrar">
                <X size={16} />
              </button>
            </DialogClose>
          </div>

          {/* Búsqueda Google Places */}
          <div className="mb-4">
            <p className="text-xs text-gray-400 mb-2">
              Escribí tu dirección o ciudad y seleccioná una opción para autocompletar los campos.
            </p>
            {/* Wrapper propio para ícono+input: `relative` acota el contexto de posicionamiento
                del MapPin a solo esta fila. Si el dropdown de sugerencias compartiera este mismo
                `relative` (al estar ahora en flujo normal, no absolute), el `top-1/2` del ícono se
                centraría respecto a ícono+input+dropdown juntos, quedando congelado a mitad de la
                lista en vez de sobre el input. */}
            <div className="relative">
              <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                ref={searchInputRef}
                data-testid="timezone-location-search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                placeholder={mapsReady ? 'Ej: Buenos Aires, Argentina' : 'Cargando buscador…'}
                disabled={!mapsReady}
                className={`${FIELD_CLASS} pl-9 disabled:opacity-50 disabled:cursor-not-allowed`}
              />
            </div>
            {showSuggestions && suggestions.length > 0 && (
              // En flujo normal (no absolute): la lista de sugerencias infla el alto de la
              // card en vez de flotar por fuera y recortarse contra los bordes del modal.
              // Tope propio (max-h-44) + scroll interno cuando hay muchas opciones; el
              // límite global de la card (max-h-[90svh] en el wrapper) sigue siendo la
              // última red de contención si el contenido total excede el viewport.
              <div
                data-testid="timezone-location-suggestions"
                className="w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-44 overflow-y-auto"
              >
                {suggestions.map((s) => (
                  <button
                    key={s.place_id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    onMouseDown={() => handleSelectSuggestion(s)}
                  >
                    {s.description}
                  </button>
                ))}
              </div>
            )}
          </div>

          {country !== '' && (
            <>
              {/* País */}
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-800 mb-1">País</p>
                <input type="text" value={country} readOnly className={`${FIELD_CLASS} bg-gray-50 cursor-default select-none`} />
              </div>

              {/* Estado / Provincia */}
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-800 mb-1">{getSubdivisionLabel(countryCode)}</p>
                <input type="text" value={stateRegion} readOnly className={`${FIELD_CLASS} bg-gray-50 cursor-default select-none`} />
              </div>

              {/* Ciudad */}
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-800 mb-1">Ciudad</p>
                <input type="text" value={city} readOnly className={`${FIELD_CLASS} bg-gray-50 cursor-default select-none`} />
              </div>

              {/* Huso horario */}
              <div className="mb-5">
                <p className="text-sm font-semibold text-gray-800 mb-1">Huso horario</p>
                {loadingTimezone ? (
                  <div className="flex items-center h-10 px-3 border border-gray-200 rounded-xl text-sm text-gray-400 bg-gray-50">
                    Detectando huso horario…
                  </div>
                ) : (
                  <input
                    type="text"
                    value={formatTimezoneName(timezone)}
                    readOnly
                    placeholder="Se detecta al seleccionar una ubicación"
                    className={`${FIELD_CLASS} bg-gray-50 cursor-default select-none`}
                  />
                )}
                {(() => {
                  if (!timezone || !currentTimezone || timezone === currentTimezone) return null;
                  const newOffset = getTimezoneInfo(timezone).offset;
                  const currentOffset = getTimezoneInfo(currentTimezone).offset;
                  if (newOffset !== currentOffset) return null;
                  return (
                    <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                      <AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-700">
                        Esta zona tiene el mismo horario que tu configuración actual ({currentOffset}). Podés guardarlo igual si querés reflejar tu ubicación real.
                      </p>
                    </div>
                  );
                })()}
              </div>

              {/* Preview de agenda */}
              {(() => {
                if (!timezone || !currentTimezone || timezone === currentTimezone || loadingTimezone) return null;
                const currentOffset = getTimezoneInfo(currentTimezone).offset;
                const newOffset = getTimezoneInfo(timezone).offset;
                if (currentOffset === newOffset) return null;
                const currentInfo = getTimezoneDisplayInfo(currentTimezone);
                const newInfo = getTimezoneDisplayInfo(timezone);
                const preview = buildPreview(currentTimezone, timezone);
                const firstRow = preview[0];
                const [fh, fm] = firstRow.fromTime.split(':').map(Number);
                const [th, tm] = firstRow.toTime.split(':').map(Number);
                const diffMins = (th * 60 + tm) - (fh * 60 + fm) + firstRow.dayDiff * 24 * 60;
                const absMins = Math.abs(diffMins);
                const diffH = Math.floor(absMins / 60);
                const diffM = absMins % 60;
                const sign = diffMins > 0 ? '+' : '-';
                const diffLabel = diffM === 0 ? `${sign}${diffH} hs` : `${sign}${diffH}:${String(diffM).padStart(2, '0')} hs`;
                const diffColor = 'text-green-500';
                const diffBg = 'bg-green-50 border-green-200';
                return (
                  <div className="mt-4 rounded-xl border border-violet-100 bg-violet-50/60 px-4 py-3">
                    <div className="flex items-center gap-1.5 mb-3">
                      <CalendarClock size={13} className="text-violet-500 shrink-0" />
                      <p className="text-xs font-semibold text-violet-700">¿Cómo se verán tus sesiones?</p>
                    </div>
                    <div className="grid grid-cols-[1fr_auto_1fr] gap-x-2 items-center">
                      <p className="text-[11px] font-medium text-gray-500 truncate">{currentInfo.label}</p>
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full border ${diffColor} ${diffBg}`}>{diffLabel}</span>
                      <p className="text-[11px] font-medium text-gray-500 truncate text-right">{newInfo.label}</p>
                    </div>
                    <div className="grid grid-cols-[1fr_auto_1fr] gap-x-2 items-center mb-2">
                      <p className="text-[10px] text-gray-400">{currentInfo.offset}</p>
                      <span />
                      <p className="text-[10px] text-gray-400 text-right">{newInfo.offset}</p>
                    </div>
                    <div className="border-t border-violet-100 pt-2 space-y-1.5">
                      {preview.map(({ fromTime, toTime, dayDiff }, i) => (
                        <div key={i} className="grid grid-cols-[1fr_auto_1fr] gap-x-2 items-center">
                          <p className="text-sm font-semibold text-gray-700">{fromTime}</p>
                          <ArrowRight size={18} className={`${diffColor} shrink-0`} />
                          <p className={`text-sm font-semibold text-right ${diffColor}`}>
                            {dayDiff !== 0 && (
                              <span className="mr-1 text-[10px] font-normal">
                                {dayDiff > 0 ? '+1 día' : '-1 día'}
                              </span>
                            )}
                            {toTime}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <hr className="mb-4 border-gray-100" />
            </>
          )}

          {/* Consentimiento — solo visible cuando hay un nuevo timezone seleccionado */}
          {timezone && (!currentTimezone || timezone !== currentTimezone) && (
            <div className="rounded-2xl bg-purple-50 border border-purple-100 px-5 py-4 mb-4">
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Entiendo que al actualizar mi zona horaria, los horarios de mis sesiones en la plataforma se mostrarán según la nueva configuración.
              </p>
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <div className="relative w-4 h-4 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className="w-4 h-4 rounded border border-gray-300 bg-white peer-checked:bg-violet-600 peer-checked:border-violet-600 flex items-center justify-center transition-colors">
                    {agreed && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden>
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-sm font-medium text-gray-700">Entiendo y acepto el cambio <span className="text-primary">*</span></span>
              </label>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <Button variant="outline" onClick={onClose} disabled={saving} size="sm">
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving || !timezone || !agreed} size="sm">
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TimezoneModal;
